import React, { useEffect, useState, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { PlusOutlined, ArrowLeftOutlined, DownloadOutlined } from "@ant-design/icons";
import { Button, Card, Col, Row, Table, message, Tag, Typography, DatePicker, Select, Space, Tooltip } from "antd"; // Ajout Tag et Typography
import { useDispatch, useSelector } from "react-redux";
import { getColumnsByType } from "./columns";
import { getAssignedPersons } from "../../../redux/slices/persons/personsThunk";
import { disassociatePersonsFromDelegate, exportAssignedService } from "../../../services/delegateService";
import Filter from "../../../components/filter/Filter";
import { getGovernments } from "../../../redux/slices/governments/governmentsThunk";
import { getSpecialtysList } from "../../../redux/slices/specialtys/specialtysThunk";
import { getRegions } from "../../../redux/slices/regions/regionsThunk";
import { toast } from "react-toastify";
import CustomModal from "../../../components/modal";
import dayjs from "dayjs"; // Assurez-vous que dayjs est disponible

const { Text } = Typography;
const { RangePicker } = DatePicker;

const AssignedListDelegatesPage = () => {
  const { 
    assignedPersons: persons_data, 
    loading: persons_loading 
  } = useSelector((state) => state.persons);
  
  const { governments: governmentsData, loading: government_loading } = useSelector((state) => state.governments);
  const { regions: regions_data, loading: region_loading } = useSelector((state) => state.regions);
  const { specialtys: specialtysData, loading: specialty_loading } = useSelector((state) => state.specialtys);

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const delegateId = useMemo(() => parseInt(location.pathname.split("/").pop(), 10), [location.pathname]);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 10,
    type: [1], 
    region: [],
    specialty: [],
    government: [],
    search: "",
    visitStatus: 'all', 
    startDate: '2026-01-01', 
    endDate: dayjs().format('YYYY-MM-DD'), 
  });

  const navigateTo = (path) => history.push(path);

  const handleExport = async () => {
    try {
        message.loading({ content: "Préparation de l'export...", key: "export" });
        
        const response = await exportAssignedService(delegateId, {
            visitStatus: queryParams.visitStatus,
            startDate: queryParams.startDate,
            endDate: queryParams.endDate,
            search: queryParams.search,
            typeIds: queryParams.type.join(','),
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Export_Assignation_${dayjs().format('YYYY-MM-DD')}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        message.success({ content: "Export terminé", key: "export" });
    } catch (e) {
        message.error({ content: "Erreur export", key: "export" });
    }
};

  // --- NOUVELLE FONCTION : AFFICHAGE DE LA LIGNE ÉTENDUE (+) ---
  const expandedRowRender = (record) => {
    const filterStart = dayjs(queryParams.startDate).startOf('day');
    const filterEnd = dayjs(queryParams.endDate).endOf('day');

    const subColumns = [
      { title: "Délégués Assignés", dataIndex: "name", key: "name", render: (text) => <Tag color="blue" style={{ fontWeight: 'bold' }}>{text}</Tag> },
      {
        title: "Cycle d'assignation",
        dataIndex: "cycle",
        key: "cycle",
        render: (cycles) => (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {cycles && cycles.length > 0 
              ? cycles.map(c => (
                  <Tag key={c.id} color="green" style={{ fontSize: '11px' }}>{c.name}</Tag>
                )) 
              : <Text type="secondary" italic style={{ fontSize: '11px' }}>Aucun cycle sur cette période</Text>}
          </div>
        )
      },
      { title: "Dates des visites", dataIndex: "visits", key: "visits", render: (dates) => <Text type="secondary" strong>{dates.length > 0 ? dates.join(" | ") : "Pas de visite"}</Text> },
    ];

    const subData = record.delegates?.map((del) => {
      // 1. Filtrage des rapports de visite (Déjà OK)
      let delVisits = record.reports?.filter((r) => r.delegate?.id === del.id) || [];
      const filteredVisits = delVisits.filter(r => {
        const reportDate = dayjs(r.date);
        return (reportDate.isAfter(filterStart) || reportDate.isSame(filterStart)) && 
               (reportDate.isBefore(filterEnd) || reportDate.isSame(filterEnd));
      });

      // 2. FILTRAGE DES CYCLES PAR LEURS SEMAINES
      let delCycles = del.cycles || [];
      const filteredCycles = delCycles.filter(c => {
        // Un cycle peut avoir plusieurs semaines (weeks)
        if (!c.weeks || c.weeks.length === 0) return false;

        // On trie les semaines pour trouver la date de début (S1) et la date de fin (S4)
        const sortedWeeks = [...c.weeks].sort((a, b) => dayjs(a.start_date).diff(dayjs(b.start_date)));
        
        const cycleStart = dayjs(sortedWeeks[0].start_date).startOf('day');
        const cycleEnd = dayjs(sortedWeeks[sortedWeeks.length - 1].end_date).endOf('day');

        // CONDITION D'AFFICHAGE : Le cycle doit être actif pendant la période filtrée
        const startsBeforeFilterEnds = cycleStart.isBefore(filterEnd) || cycleStart.isSame(filterEnd);
        const endsAfterFilterStarts = cycleEnd.isAfter(filterStart) || cycleEnd.isSame(filterStart);

        return startsBeforeFilterEnds && endsAfterFilterStarts;
      });

      return {
        key: del.id,
        name: `${del.firstname} ${del.lastname}`.toUpperCase(),
        cycle: filteredCycles,
        visits: filteredVisits.map((r) => dayjs(r.date).format("DD/MM/YYYY")),
      };
    });

    return (
      <Table columns={subColumns} dataSource={subData} pagination={false} size="small" style={{ margin: '10px 0', backgroundColor: '#fafafa', border: '1px solid #f0f0f0' }} />
    );
  };

  const handleDelete = async () => {
    try {
      await disassociatePersonsFromDelegate(delegateId, selectedRowKeys);
      toast.success("Personnes dissociées avec succès");
      setSelectedRowKeys([]);
      setSelectedNames([]);
      setIsModalOpen(false);
      dispatch(getAssignedPersons({
        delegateId,
        page: queryParams.page,
        limit: queryParams.limit,
        filter: {
          specialty: queryParams.specialty,
          region: queryParams.region,
          government: queryParams.government,
          type: queryParams.type,
          search: queryParams.search,
        },
      }));
    } catch (error) {
      toast.error("Erreur lors de la dissociation.");
      console.error(error);
    }
  };

  const handlePageChange = (page, pageSize) => {
    setQueryParams(prev => ({ ...prev, page, limit: pageSize }));
  };

  const handleSelect = (ids, key) => {
    setQueryParams(prev => ({ ...prev, [key]: ids, page: 1 }));
  };
  
  const handleSearchChange = (value) => {
    setQueryParams(prev => ({ ...prev, search: value, page: 1 }));
  };

  const createOptions = (data, labelKey = "name", valueKey = "id") => {
    return data?.map((item) => ({
      id: item[valueKey],
      label: item[labelKey],
      value: item[valueKey],
    })) || [];
  };
  
  const optionsLists = useMemo(() => [
    { name: "Type", placeholder: "Type", key: "type", multiple: true, options: [{id: 1, label: "Médecin", value: 1 }, {id: 2, label: "Pharmacien", value: 2 }, {id: 3, label: "Grossiste", value: 3 },], },
    { name: "Spécialités", placeholder: "Spécialités", key: "specialty", multiple: true, isLoading: specialty_loading, options: createOptions(specialtysData?.data, "name", "id"), },
    { name: "Gouvernorat", placeholder: "Gouvernorat", key: "government", multiple: true, isLoading: government_loading, options: createOptions(governmentsData?.data, "name", "id"), },
    { name: "Région", placeholder: "Région", key: "region", multiple: true, isLoading: region_loading, options: createOptions(regions_data?.data, "name", "id"), },
  ], [specialtysData, governmentsData, regions_data, specialty_loading, government_loading, region_loading]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys, selectedRows) => {
      setSelectedRowKeys(keys);
      setSelectedNames(selectedRows.map((item) => `${item.firstname} ${item.lastname}`));
    },
  };

  useEffect(() => {
    dispatch(getGovernments({ page: 1, limit: 100 }));
    dispatch(getSpecialtysList({ page: 1, itemsPerPage: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (queryParams.government.length > 0) {
      dispatch(getRegions({ page: 1, itemsPerPage: 200, governmentIds: queryParams.government }));
    }
  }, [dispatch, queryParams.government]);

  useEffect(() => {
    if (!isNaN(delegateId)) {
      dispatch(getAssignedPersons({
        delegateId,
        page: queryParams.page,
        limit: queryParams.limit,
        filter: {
          specialty: queryParams.specialty,
          region: queryParams.region,
          government: queryParams.government,
          type: queryParams.type,
          search: queryParams.search,
          visitStatus: queryParams.visitStatus,
          startDate: queryParams.startDate,
          endDate: queryParams.endDate,
        },
      }));
    }
  }, [dispatch, delegateId, queryParams]);

  const handleAddClick = () => {
    const currentType = queryParams.type && queryParams.type.length > 0 ? queryParams.type[0] : 1;
    navigateTo(`/assigner/personne/${delegateId}?type=${currentType}`);
  };

  return (
    <div className="tabled">
       
       <style>{`
        .ant-table-thead > tr > th, 
        .ant-table-tbody > tr > td { 
          padding: 8px 4px !important; /* Réduit le padding (Défaut AntD est 16px) */
          font-size: 12px !important;  /* Réduit légèrement la taille de police */
        }
        .ant-table-cell-ellipsis {
          cursor: pointer;
        }
      `}</style>

      <Row gutter={[24, 0]}>
        <Col xs={24} xl={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title={
              <>
                <Button type="link" onClick={() => navigateTo("/délégués")} style={{ marginRight: "16px" }}>
                  <ArrowLeftOutlined /> Retour
                </Button>
                Liste d'assignation des délégués
              </>
            }
            extra={
              <Button type="primary" onClick={handleAddClick}>
                <PlusOutlined /> Ajouter
              </Button>
            }
          />
          <Card bordered={false} className="criclebox tablespace mb-24" title={
    <div style={{ padding: "10px" }}>
        <Row gutter={[16, 16]} align="middle">
            <Col span={24}>
                <Filter
                    searchPlaceholder="Rechercher"
                    optionsLists={optionsLists}
                    onSelect={handleSelect}
                    onSearchValue={handleSearchChange}
                    data={queryParams}
                />
            </Col>
            <Col span={24}>
                <Space size="middle">
                    <Text strong>Période de visite :</Text>
                    <RangePicker 
                        defaultValue={[dayjs('2026-01-01'), dayjs()]}
                        onChange={(dates) => {
                            setQueryParams(prev => ({
                                ...prev,
                                startDate: dates ? dates[0].format('YYYY-MM-DD') : '2026-01-01',
                                endDate: dates ? dates[1].format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
                                page: 1
                            }));
                        }}
                    />
                    
                    <Text strong>Statut Visite :</Text>
                    <Select 
                        defaultValue="all" 
                        style={{ width: 150 }}
                        onChange={(val) => setQueryParams(prev => ({ ...prev, visitStatus: val, page: 1 }))}
                        options={[
                            { value: 'all', label: 'Tout' },
                            { value: 'visited', label: 'Visité' },
                            { value: 'not_visited', label: 'Non visité' },
                        ]}
                    />

                    <Button 
                        icon={<DownloadOutlined />} 
                        onClick={handleExport}
                    >
                        Exporter la liste
                    </Button>
                </Space>
            </Col>
        </Row>
        
        <div style={{ marginTop: "16px" }}>
            <Button type="primary" onClick={() => setIsModalOpen(true)} disabled={selectedRowKeys.length === 0}>
                Dissocier la sélection
            </Button>
        </div>
    </div>
}>
            <Table
                rowSelection={rowSelection}
                columns={getColumnsByType(queryParams.type, navigateTo, setSelectedRowKeys)}
                dataSource={persons_data?.data}
                loading={persons_loading}
                className="ant-border-space"
                
                // --- APPLICATION DU SCROLL HORIZONTAL ---
                // On additionne les largeurs définies (environ 1200-1300px au total)
                scroll={{ x: 1300 }} 
                // -----------------------------------------

                expandable={{
                  expandedRowRender,
                  rowExpandable: (record) => record.delegates && record.delegates.length > 0,
                }}
                pagination={{
                  current: queryParams.page,
                  pageSize: queryParams.limit,
                  total: persons_data?.total,
                  onChange: handlePageChange,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50"],
                }}
                rowKey={(record) => record.id}
              />
          </Card>
        </Col>
      </Row>
      <CustomModal
        title="Dissocier"
        visible={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onClose={() => setIsModalOpen(false)}
        action={handleDelete}
        actionName="Dissocier"
        body={`Voulez-vous vraiment dissocier ${selectedNames.join(', ')} ?`}
      />
    </div>
  );
};

export default AssignedListDelegatesPage;